import { Fragment, useState, useMemo, useCallback } from 'react';
import { useConfirm } from 'material-ui-confirm';
import { toast } from 'react-toastify';

// Use unified date utils (configured timezone)
import { moment } from '../../utils/dateUtils';

import { Box, Typography, Grid, Link, Tooltip, Modal, Button, CircularProgress, Backdrop } from '@mui/material';

// Icons
import CircleIcon from '@mui/icons-material/Circle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Custom Components
import { Spinner } from '..';
import TimeSlots from './Calendar/TimeSlots';
import TooltipTitle from './Calendar/TooltipTitle';
import CancelModal from './CancelModal';
import PreferredTimeConfirmDialog from './Calendar/PreferredTimeConfirmDialog';

// Utils
import messages from '../../static/messages';
import { 
  defineTime, 
  convertTime, 
  matchesPreferredTime, 
  formatPreferredTime,
  sortAppointmentsByColumn,
} from './Calendar/utils';

// Services
import appointmentsServices from '../../services/appointmentsServices';

// Styles
import { modal_style } from '../../static/styles';
import { button } from '../../static/theme-styles';
import {
  column_heading,
  time_slots_styles,
  links_styles,
  queue_row_styles,
  queue_item_styles,
  calendar_grid_styles,
  status_icon_styles,
} from './Calendar/styles';

// Re-export for backwards compatibility
export { defineTime };
export { column_heading, time_slots_styles };

// Function to get contrasting border color
const getContrastBorderColor = (bgColor) => {
  if (!bgColor) return { border: '#000000', shadow: 'rgba(0, 0, 0, 0.5)' };
  
  const colorMap = {
    red: { r: 255, g: 0, b: 0 },
    pink: { r: 255, g: 192, b: 203 },
    lightpink: { r: 255, g: 182, b: 193 },
    hotpink: { r: 255, g: 105, b: 180 },
    deeppink: { r: 255, g: 20, b: 147 },
    salmon: { r: 250, g: 128, b: 114 },
    coral: { r: 255, g: 127, b: 80 },
    tomato: { r: 255, g: 99, b: 71 },
    orange: { r: 255, g: 165, b: 0 },
    darkorange: { r: 255, g: 140, b: 0 },
    orangered: { r: 255, g: 69, b: 0 },
    crimson: { r: 220, g: 20, b: 60 },
    maroon: { r: 128, g: 0, b: 0 },
    darkred: { r: 139, g: 0, b: 0 },
    firebrick: { r: 178, g: 34, b: 34 },
    indianred: { r: 205, g: 92, b: 92 },
    lightcoral: { r: 240, g: 128, b: 128 },
    green: { r: 0, g: 128, b: 0 },
    lime: { r: 0, g: 255, b: 0 },
    lightgreen: { r: 144, g: 238, b: 144 },
    blue: { r: 0, g: 0, b: 255 },
    lightblue: { r: 173, g: 216, b: 230 },
    yellow: { r: 255, g: 255, b: 0 },
    purple: { r: 128, g: 0, b: 128 },
    violet: { r: 238, g: 130, b: 238 },
    cyan: { r: 0, g: 255, b: 255 },
    teal: { r: 0, g: 128, b: 128 },
    magenta: { r: 255, g: 0, b: 255 },
    brown: { r: 165, g: 42, b: 42 },
    white: { r: 255, g: 255, b: 255 },
    black: { r: 0, g: 0, b: 0 },
    gray: { r: 128, g: 128, b: 128 },
    grey: { r: 128, g: 128, b: 128 },
    lightgray: { r: 211, g: 211, b: 211 },
  };
  
  let r, g, b;
  const lowerColor = bgColor.toLowerCase().trim();
  
  if (colorMap[lowerColor]) {
    ({ r, g, b } = colorMap[lowerColor]);
  } else if (bgColor.startsWith('#')) {
    const hex = bgColor.slice(1);
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length === 6) {
      r = parseInt(hex.slice(0, 2), 16);
      g = parseInt(hex.slice(2, 4), 16);
      b = parseInt(hex.slice(4, 6), 16);
    }
  } else if (bgColor.startsWith('rgb')) {
    const match = bgColor.match(/\d+/g);
    if (match && match.length >= 3) {
      [r, g, b] = match.map(Number);
    }
  }
  
  if (r === undefined) {
    return { border: '#000000', shadow: 'rgba(0, 0, 0, 0.5)' };
  }
  
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  const isRedFamily = r > 150 && g < 150 && b < 150;
  const isPinkFamily = r > 200 && g > 100 && g < 220 && b > 100 && b < 220;
  
  if (isRedFamily || isPinkFamily) {
    return { border: '#00BCD4', shadow: 'rgba(0, 188, 212, 0.6)' };
  } else if (luminance > 0.5) {
    return { border: '#C62828', shadow: 'rgba(198, 40, 40, 0.5)' };
  } else {
    return { border: '#FFD600', shadow: 'rgba(255, 214, 0, 0.6)' };
  }
};

const Calendar = ({ 
  date, 
  appointments, 
  queue, 
  baseColumns,  // NEW: base column structure without appointments
  refreshAppointments, // NEW: callback to refresh appointments
  workHours = { startHour: 7, endHour: 22, workDays: [1, 2, 3, 4, 5, 6] },
  technicianUnavailability = {},
  isSuperAdmin = false,
  onColumnReorder = null,
  onEditTimeBlock = null,
}) => {
  const [currentItem, setCurrentItem] = useState(null);
  const [currentCol, setCurrentCol] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dayOffModal, setDayOffModal] = useState(false);
  const [dayOffId, setDayOffId] = useState(null);
  const [currentAppointment, setCurrentAppointment] = useState(null);
  const [cancelModal, setCancelModal] = useState(false);
  const [cancelData, setCancelData] = useState();
  
  // Column drag state
  const [draggedColumnIndex, setDraggedColumnIndex] = useState(null);
  
  // Appointment drag state - to hide tooltip during drag
  const [isDragging, setIsDragging] = useState(false);
  
  // Preferred time confirmation
  const [preferredTimeDialog, setPreferredTimeDialog] = useState(false);
  const [pendingDrop, setPendingDrop] = useState(null);

  const confirm = useConfirm();
  // moment.tz.setDefault is now configured in dateUtils.js

  const { startHour, endHour } = workHours;

  // Check if technician is unavailable on current date
  const isTechnicianUnavailable = useCallback((techId) => {
    if (!techId || !technicianUnavailability[techId]) return false;
    
    const currentDate = moment(date).startOf('day');
    const periods = technicianUnavailability[techId];
    
    for (const period of periods) {
      const from = moment(period.unavailable_from).startOf('day');
      const toMoment = moment(period.unavailable_to);
      const toDay = toMoment.hour() < 12 ? moment(toMoment).subtract(1, 'day').startOf('day') : moment(toMoment).startOf('day');
      
      if (currentDate.isSameOrAfter(from) && currentDate.isSameOrBefore(toDay)) {
        return true;
      }
    }
    return false;
  }, [date, technicianUnavailability]);

  // Get unavailability reason
  const getUnavailabilityReason = useCallback((techId) => {
    if (!techId || !technicianUnavailability[techId]) return null;
    
    const currentDate = moment(date).startOf('day');
    const periods = technicianUnavailability[techId];
    
    for (const period of periods) {
      const from = moment(period.unavailable_from).startOf('day');
      const toMoment = moment(period.unavailable_to);
      const toDay = toMoment.hour() < 12 ? moment(toMoment).subtract(1, 'day').startOf('day') : moment(toMoment).startOf('day');
      
      if (currentDate.isSameOrAfter(from) && currentDate.isSameOrBefore(toDay)) {
        return period.reason || 'OFF';
      }
    }
    return null;
  }, [date, technicianUnavailability]);

  // CRITICAL: Calculate display columns with appointments using useMemo
  // This replaces the problematic useEffect + ref approach
  const displayColumns = useMemo(() => {
    if (!baseColumns || !appointments) return null;
    
    console.log('[Calendar] Computing displayColumns, appointments:', appointments.length);
    
    // Deep copy base columns
    const columnsCopy = baseColumns.map(col => ({
      ...col,
      info: { ...col.info },
      items: col.items ? col.items.map((item, index) => ({
        dump: true,
        time: defineTime(index, startHour),
      })) : undefined
    }));

    // Distribute appointments to columns
    columnsCopy.forEach((col) => {
      // Technician columns
      if (col.info.tech_id) {
        const relatedItems = appointments.filter(
          (el) => el.user?.id === col.info.tech_id && el.type?.name !== 'Installation' && el.status !== 'Cancelled',
        );

        let withClonedDayOff = [...relatedItems];

        relatedItems.forEach((el) => {
          if (el.type?.name === 'Day Off' || el.type?.name === 'Time Block') {
            // The end hour is EXCLUSIVE, as everywhere in calendars: a block
            // 11:00–12:00 occupies only the 11:00 cell. A partial last hour
            // (e.g. ...–12:30) still shows the 12:00 cell. Matches the
            // server-side availability check (interval containment).
            const hoursSpan = moment(el.endTime).diff(el.startTime, 'hours', true);
            for (let i = 1; i < hoursSpan; i++) {
              withClonedDayOff.push({
                ...el,
                startTime: moment(el.startTime).add(i, 'hours').format(),
              });
            }
          }
        });

        if (withClonedDayOff.length) sortAppointmentsByColumn(col, withClonedDayOff, startHour);
      }

      // Installations
      if (col.info.name === 'Installations') {
        const relatedItems = appointments.filter((el) => el.type?.name === 'Installation');
        if (relatedItems.length) sortAppointmentsByColumn(col, relatedItems, startHour);
      }

    });

    return columnsCopy;
  }, [baseColumns, appointments, startHour]);

  const onDayOffModalOpen = (id) => {
    setDayOffId(id);
    getDayOffInfo(id);
    setDayOffModal(true);
  };

  const onDayOffModalClose = () => {
    setDayOffId(null);
    setCurrentAppointment(null);
    setDayOffModal(false);
  };

  const onTimeBlockClick = (id) => {
    const allItems = displayColumns?.flatMap(col => col.items?.filter(item => !item.dump)) || [];
    const blockInfo = allItems.find(item => item.id === id);
    
    if (onEditTimeBlock && blockInfo) {
      onEditTimeBlock(blockInfo);
    }
  };

  const onCancelModalClose = () => {
    setCancelData();
    setCancelModal(false);
  };

  const onCancelAppointment = (id, start, end) => {
    setCancelModal(true);
    setCancelData({ id, start, end });
  };

  // Column drag handlers
  const handleColumnDragStart = (e, colIndex) => {
    if (!isSuperAdmin) return;
    e.dataTransfer.effectAllowed = 'move';
    setDraggedColumnIndex(colIndex);
    e.target.style.opacity = '0.5';
  };

  const handleColumnDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedColumnIndex(null);
  };

  const handleColumnDragOver = (e, colIndex) => {
    if (!isSuperAdmin || draggedColumnIndex === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleColumnDrop = (e, targetIndex) => {
    if (!isSuperAdmin || draggedColumnIndex === null) return;
    e.preventDefault();
    
    if (draggedColumnIndex === targetIndex) {
      setDraggedColumnIndex(null);
      return;
    }

    const techColumns = displayColumns.filter(col => col.info.tech_id);
    
    if (draggedColumnIndex >= techColumns.length || targetIndex >= techColumns.length) {
      setDraggedColumnIndex(null);
      return;
    }

    const newTechColumns = [...techColumns];
    const [removed] = newTechColumns.splice(draggedColumnIndex, 1);
    newTechColumns.splice(targetIndex, 0, removed);

    const newOrder = newTechColumns.map(col => col.info.tech_id);
    if (onColumnReorder) {
      onColumnReorder(newOrder);
    }

    setDraggedColumnIndex(null);
  };

  // Drag handlers for appointments
  const dragStartHandler = (e, col, item) => {
    setCurrentCol(col);
    setCurrentItem(item);
    setIsDragging(true); // Hide tooltips during drag
  };

  const dragEndHandler = (e) => {
    e.target.style.boxShadow = 'none';
    setCurrentCol(null);
    setCurrentItem(null);
    setIsDragging(false); // Show tooltips again
  };

  const dragOverHandler = (e) => {
    e.preventDefault();
    const classes = e.target.className.split(' ');
    if (classes.includes('item')) e.target.style.boxShadow = 'inset 0 0 50px 0 #ccc';
  };

  const dragLeaveHandler = (e) => (e.target.style.boxShadow = 'none');

  const executeDrop = async (dropData) => {
    const { col, currentItem, dropIndex } = dropData;
    
    setIsLoading(true);
    
    let timeDifference = 60;
    if (currentItem.startTime && currentItem.endTime) {
      const calculatedDiff = moment(currentItem.endTime).diff(currentItem.startTime, 'minutes', true);
      if (calculatedDiff && calculatedDiff > 0 && !isNaN(calculatedDiff)) {
        timeDifference = calculatedDiff;
      }
    }

    const newStartTime = moment(date).set({
      hour: convertTime(defineTime(dropIndex, startHour)).h,
      minute: convertTime(defineTime(dropIndex, startHour)).m,
      second: '0',
    });

    const newEndTime = moment(date)
      .set({
        hour: convertTime(defineTime(dropIndex, startHour)).h,
        minute: convertTime(defineTime(dropIndex, startHour)).m,
        second: '0',
      })
      .add(timeDifference || 60, 'minutes');

    await reassign(
        currentItem.id,
        col.info.tech_id,
        // Convert LA time to UTC properly (newStartTime.utc() converts, moment.utc() interprets)
        newStartTime.clone().utc().format(),
        newEndTime.clone().utc().format(),
        'Active',
      );
  };

  const dropHandler = (e, col, item) => {
    e.preventDefault();
    e.target.style.boxShadow = 'none';

    if (isLoading) {
      toast.warning('Please wait for the previous operation to complete');
      return;
    }

    if (col.info.tech_id && isTechnicianUnavailable(col.info.tech_id)) {
      const reason = getUnavailabilityReason(col.info.tech_id);
      toast.warning(`${col.info.name} is unavailable: ${reason || 'OFF'}`);
      return;
    }

    if (currentItem?.type?.name === 'Installation' && col.info.name !== 'Installations') {
      toast.warning('Appointment with type Installation cannot be moved to this column!');
      return;
    }

    if (currentItem?.type?.name !== 'Installation' && col.info.name === 'Installations') {
      toast.warning('This column can only have appointments with the Installation type.');
      return;
    }

    if (!item.dump) return;

    const dropIndex = col.items.indexOf(item);
    
    const newStartTime = moment(date).set({
      hour: convertTime(defineTime(dropIndex, startHour)).h,
      minute: convertTime(defineTime(dropIndex, startHour)).m,
      second: '0',
    });
    
    let timeDifference = 60;
    if (currentItem.startTime && currentItem.endTime) {
      const calculatedDiff = moment(currentItem.endTime).diff(currentItem.startTime, 'minutes', true);
      if (calculatedDiff && calculatedDiff > 0 && !isNaN(calculatedDiff)) {
        timeDifference = calculatedDiff;
      }
    }
    
    const newEndTime = moment(newStartTime).add(timeDifference, 'minutes');

    const conflicts = [];

    if (currentItem.preferredDate || currentItem.preferredStartTime || currentItem.preferredEndTime) {
      const matchesTime = matchesPreferredTime(
        currentItem, 
        newStartTime.clone().utc().format(), 
        newEndTime.clone().utc().format()
      );

      if (!matchesTime) {
        conflicts.push({
          type: 'time',
          message: 'Time does not match customer preference',
          preferred: formatPreferredTime(currentItem),
          actual: `${newStartTime.format('MM/DD/YYYY hh:mm A')} - ${newEndTime.format('h:mm A')}`,
        });
      }
    }

    if (currentItem.preferredUser && col.info.tech_id) {
      if (currentItem.preferredUser.id !== col.info.tech_id) {
        conflicts.push({
          type: 'technician',
          message: 'Technician does not match customer preference',
          preferred: currentItem.preferredUser.fullName || currentItem.preferredUser.firstName + ' ' + currentItem.preferredUser.lastName,
          actual: col.info.name,
        });
      }
    }

    if (conflicts.length > 0) {
      setPendingDrop({
        col,
        item,
        currentItem,
        currentCol,
        dropIndex,
        newStartTime: newStartTime.format('MM/DD/YYYY hh:mm A'),
        newEndTime: newEndTime.format('h:mm A'),
        conflicts,
      });
      setPreferredTimeDialog(true);
      return;
    }

    executeDrop({ col, item, currentItem, currentCol, dropIndex });
  };

  const handlePreferredTimeConfirm = () => {
    setPreferredTimeDialog(false);
    if (pendingDrop) {
      executeDrop(pendingDrop);
      setPendingDrop(null);
    }
  };

  const handlePreferredTimeCancel = () => {
    setPendingDrop(null);
    setPreferredTimeDialog(false);
  };

  const dropCardHandler = (e, col) => {
    if (currentCol?.info.name === 'Queue' && col.info.name === 'Queue') return;
    if (isLoading) return;

    setIsLoading(true);
    reassign(currentItem.id, null, null, null, 'Active');
  };

  const handleRemoveDayOff = () => {
    confirm({
      title: messages.confirm_message.remove,
      description: messages.confirm_message.remove_description('Day Off'),
    })
      .then(() => removeAppointment(dayOffId))
      .catch(() => toast.info(messages.cancel_message));
  };

  // Async functions
  const reassign = async (aptmID, techID, newStartTime, newEndTime, status, cancelReason) => {
    const updatedAppointment = {
      ...(techID && techID > 0
        ? { user: { id: techID } }
        : { user: { id: null } }),
      startTime: newStartTime ? newStartTime : null,
      endTime: newEndTime ? newEndTime : null,
      ...(status && { status }),
      ...(cancelReason && { reasons: [{ note: cancelReason }] }),
    };

    try {
      const response = await appointmentsServices.update(aptmID, updatedAppointment);

      if (response.status === 200) {
        toast.success('Appointment updated successfully');
        refreshAppointments();
        setCancelData();
        setCancelModal(false);
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getDayOffInfo = async (id) => {
    try {
      const response = await appointmentsServices.getAppointmentById(id);

      if (response.status === 200) {
        setCurrentAppointment({
          startTime: moment(response.data.startTime).format('MM/DD/YYYY hh:mm a'),
          endTime: moment(response.data.endTime).format('MM/DD/YYYY hh:mm a'),
          notes: response.data.notes,
          user: response.data.user,
          createdBy: response.data.createdBy,
          id: response.data.id,
        });
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const removeAppointment = async (id) => {
    try {
      const response = await appointmentsServices.remove(id);

      if (response.status === 200) {
        toast.success('Day Off successfully deleted!');
        onDayOffModalClose();
        refreshAppointments();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const cancellation = async () => {
    reassign(cancelData.id, null, cancelData.start, cancelData.end, 'Cancelled', cancelData.reason);
  };

  // Show spinner while computing columns
  if (!displayColumns) return <Spinner />;

  return (
    <Fragment>
      <Backdrop open={isLoading} sx={{ zIndex: 9999, color: '#fff' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Updating appointment...
          </Typography>
        </Box>
      </Backdrop>

      {/* Queue Row */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#666' }}>
          QUEUE {queue && queue.length > 0 ? `(${queue.length} appointments)` : ''}
        </Typography>
        <Box
          sx={{
            ...queue_row_styles,
            minHeight: 50,
            border: '2px dashed #ccc',
            backgroundColor: '#f9f9f9',
          }}
          onDragOver={(e) => dragOverHandler(e)}
          onDrop={(e) => dropCardHandler(e, { info: { name: 'Queue' }, items: queue || [] })}
        >
          {queue && queue.length > 0 ? (
            queue.map((item, idx) => (
              <Box
                key={item.id || idx}
                sx={{
                  ...queue_item_styles,
                  backgroundColor: '#808080',
                  ...(item.preferredUser && {
                    border: '3px solid #FFD600',
                    boxShadow: '0 0 10px rgba(255, 214, 0, 0.7)',
                  }),
                }}
                draggable
                onDragStart={(e) => dragStartHandler(e, { info: { name: 'Queue' }, items: queue }, item)}
                onDragEnd={dragEndHandler}
              >
                <Tooltip 
                  arrow 
                  placement="right" 
                  title={<TooltipTitle info={item} />}
                  disableInteractive
                  PopperProps={{
                    sx: { display: isDragging ? 'none' : 'block' }
                  }}
                >
                  <Link href={`/appointments/${item.id}`} underline="none" sx={{ ...links_styles, fontSize: 11 }}>
                    <Typography variant="body2" sx={{ fontSize: 11, lineHeight: 1.2 }}>
                      {item.type?.name || '-'}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: 15, lineHeight: 1.2, fontWeight: 600 }}>
                      {item.property?.zipCode || item.property?.address?.match(/\d{5}/)?.[0] || ''}
                    </Typography>
                    {item.preferredUser && (
                      <Typography variant="caption" sx={{ fontSize: 9, color: '#ffcdd2', display: 'block' }}>
                        👤 {item.preferredUser.fullName || `${item.preferredUser.firstName} ${item.preferredUser.lastName}`}
                      </Typography>
                    )}
                  </Link>
                </Tooltip>
              </Box>
            ))
          ) : (
            <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic', p: 1 }}>
              Drop appointments here to add to queue
            </Typography>
          )}
        </Box>
      </Box>

      {/* Main Calendar Grid */}
      <Grid container spacing={0} wrap="nowrap" sx={calendar_grid_styles}>
        <Grid item sx={{ width: 50, minWidth: 50, flexShrink: 0 }}>
          <TimeSlots startHour={startHour} endHour={endHour} />
        </Grid>

        {displayColumns?.filter(col => col.info.name !== 'Queue').map((col, col_index) => {
          const isUnavailable = col.info.tech_id && isTechnicianUnavailable(col.info.tech_id);
          const unavailReason = isUnavailable ? getUnavailabilityReason(col.info.tech_id) : null;
          const isTechColumn = !!col.info.tech_id;
          const canDragColumn = isSuperAdmin && isTechColumn;

          return (
            <Grid item key={col_index} sx={{ flex: 1, minWidth: 0 }}>
              <Tooltip title={isUnavailable ? (unavailReason || 'OFF') : (canDragColumn ? 'Drag to reorder' : '')} placement="top">
                <Box 
                  sx={{ 
                    ...column_heading, 
                    ml: '2px',
                    ...(isUnavailable && { backgroundColor: '#9e9e9e', color: '#fff' }),
                    ...(canDragColumn && {
                      cursor: 'grab',
                      '&:hover': { backgroundColor: '#1565c0' },
                      '&:active': { cursor: 'grabbing' }
                    }),
                    ...(draggedColumnIndex === col_index && {
                      opacity: 0.5,
                      border: '2px dashed #fff',
                    })
                  }}
                  draggable={canDragColumn}
                  onDragStart={(e) => handleColumnDragStart(e, col_index)}
                  onDragEnd={handleColumnDragEnd}
                  onDragOver={(e) => handleColumnDragOver(e, col_index)}
                  onDrop={(e) => handleColumnDrop(e, col_index)}
                >
                  <Typography variant="body2" sx={{ fontSize: 14, fontFamily: 'Lato', fontWeight: 800 }}>
                    {col.info.name}
                  </Typography>
                </Box>
              </Tooltip>

              {col.items?.map((item, item_index) => {
                const hasPreferredTechnician = !item.dump && item.preferredUser;
                const preferredMatches = hasPreferredTechnician && 
                  col.info.tech_id && 
                  item.preferredUser.id === col.info.tech_id;
                
                const getBorderStyle = () => {
                  if (!hasPreferredTechnician) return null;
                  if (preferredMatches) {
                    return { border: '3px solid #4CAF50', shadow: '0 0 8px rgba(76, 175, 80, 0.6)' };
                  } else {
                    const colors = getContrastBorderColor(col.info.color);
                    return { border: `3px solid ${colors.border}`, shadow: `0 0 8px ${colors.shadow}` };
                  }
                };
                const borderStyle = getBorderStyle();

                return (
                <Box
                  key={item_index}
                  sx={{
                    ...time_slots_styles,
                    background: isUnavailable
                      ? '#e0e0e0'
                      : item.type?.name === 'Day Off' 
                        ? 'lightgray' 
                        : item.type?.name === 'Time Block'
                          ? '#9e9e9e'
                          : (!item.dump && col.info.color),
                    ml: '2px',
                    cursor: isUnavailable 
                      ? 'not-allowed' 
                      : (!item.dump && item.type?.name !== 'Day Off' && item.type?.name !== 'Time Block' ? 'grab' : 'default'),
                    opacity: isUnavailable ? 0.7 : 1,
                    ...(isUnavailable && item.dump && {
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }),
                    ...(borderStyle && {
                      border: borderStyle.border,
                      boxShadow: borderStyle.shadow,
                    }),
                  }}
                  draggable={!isUnavailable && !item.dump && item.type?.name !== 'Day Off' && item.type?.name !== 'Time Block'}
                  onDragStart={(e) => !isUnavailable && dragStartHandler(e, col, item)}
                  onDragEnd={dragEndHandler}
                  onDragOver={(e) => !isUnavailable && dragOverHandler(e)}
                  onDragLeave={dragLeaveHandler}
                  onDrop={(e) => !isUnavailable && dropHandler(e, col, item)}
                  className="item"
                >
                  {!item.dump && item.type?.name !== 'Day Off' && item.type?.name !== 'Time Block' && (
                    <Tooltip arrow placement="right" title={<TooltipTitle info={item} />} disableInteractive PopperProps={{ sx: { display: isDragging ? 'none' : 'block' } }}>
                      <Link href={`/appointments/${item?.id}`} underline="none" sx={links_styles}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: 17, lineHeight: 1.1 }}>
                          <strong>{item?.property?.zipCode || item?.property?.address?.match(/\d{5}/)?.[0] || ''}</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: 11, lineHeight: 1.1 }}>
                          {item?.type?.name}
                        </Typography>
                        {item.status !== 'Cancelled' && (
                          <Typography variant="body2" sx={{ fontSize: 10, fontWeight: 'bold', lineHeight: 1.1, color: '#fff' }}>
                            {(item?.preferredStartTime || item?.preferredEndTime) 
                              ? `${item?.preferredStartTime ? moment(item?.preferredStartTime).format('h:mm') : '?'} - ${item?.preferredEndTime ? moment(item?.preferredEndTime).format('h:mm A') : '?'}`
                              : (item?.startTime && item?.endTime ? `${moment(item?.startTime).format('h:mm')} - ${moment(item?.endTime).format('h:mm A')}` : '')}
                          </Typography>
                        )}
                        <Box sx={{ position: 'absolute', left: 2, top: 2, width: 15, height: 15 }}>
                          {item.status === 'Active' && (
                            <CircleIcon sx={{ ...status_icon_styles, color: 'green' }} />
                          )}
                          {item.status === 'In Progress' && (
                            <CircleIcon sx={{ ...status_icon_styles, color: 'orange' }} />
                          )}
                          {item.status === 'Completed' && (
                            <CheckCircleIcon sx={{ ...status_icon_styles, color: 'green' }} />
                          )}
                        </Box>
                        {item.reviewRequestSentAt && (
                          <Box sx={{
                            position: 'absolute', right: 2, top: 2,
                            width: 15, height: 15, borderRadius: '50%',
                            backgroundColor: '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.3))',
                          }}>
                            <Typography sx={{ fontSize: 10, fontWeight: 900, color: '#F4B400', lineHeight: 1 }}>G</Typography>
                          </Box>
                        )}
                      </Link>
                    </Tooltip>
                  )}
                  {item.type?.name === 'Day Off' && !isUnavailable && (
                    <Box onClick={() => onDayOffModalOpen(item.id)} sx={{ cursor: 'pointer' }}>
                      Day Off
                    </Box>
                  )}
                  {item.type?.name === 'Time Block' && !isUnavailable && (
                    <Box 
                      onClick={() => onTimeBlockClick(item.id)} 
                      sx={{ 
                        cursor: 'pointer', 
                        color: '#fff', 
                        fontWeight: 'bold',
                        fontSize: 12,
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                      }}
                    >
                      <Typography sx={{ fontSize: 11, fontWeight: 'bold' }}>🚫 BLOCKED</Typography>
                      {item.notes?.[0]?.note && (
                        <Typography sx={{ fontSize: 9, opacity: 0.9 }}>{item.notes[0].note}</Typography>
                      )}
                    </Box>
                  )}
                  {isUnavailable && item.dump && (
                    <Typography 
                      variant="body2" 
                      sx={{ color: '#757575', fontWeight: 'bold', fontSize: 11 }}
                    >
                      OFF
                    </Typography>
                  )}
                </Box>
              );
              })}
            </Grid>
          );
        })}
      </Grid>

      {/* Day Off Modal */}
      <Modal open={dayOffModal} onClose={onDayOffModalClose}>
        <Box sx={modal_style}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
            DAY OFF INFO
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Start Time: {currentAppointment?.startTime}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            End Time: {currentAppointment?.endTime}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Comment: {currentAppointment?.notes?.[0]?.note}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Created By: {currentAppointment?.createdBy?.fullName}
          </Typography>
          <Box>
            <Button variant="contained" sx={button('primary', 'secondary')} onClick={handleRemoveDayOff}>
              Remove Day Off
            </Button>
            <Button variant="contained" sx={{ ...button('secondary'), ml: 2 }} onClick={onDayOffModalClose}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Cancel Modal */}
      <CancelModal
        open={cancelModal}
        close={onCancelModalClose}
        data={cancelData}
        setCancelData={setCancelData}
        cancellation={cancellation}
      />

      {/* Preference Mismatch Confirmation Dialog */}
      <PreferredTimeConfirmDialog
        open={preferredTimeDialog}
        onClose={handlePreferredTimeCancel}
        onConfirm={handlePreferredTimeConfirm}
        appointmentInfo={pendingDrop ? 
          `${pendingDrop.currentItem.type?.name || 'Appointment'} - ${pendingDrop.currentItem.property?.address || 'No address'}` 
          : ''}
        preferredTimeInfo={pendingDrop ? formatPreferredTime(pendingDrop.currentItem) : ''}
        newTimeInfo={pendingDrop ? `${pendingDrop.newStartTime} - ${pendingDrop.newEndTime}` : ''}
        conflicts={pendingDrop?.conflicts || []}
      />
    </Fragment>
  );
};

export default Calendar;
